import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CheckIcon, PlusIcon, TrashIcon, UploadIcon } from "@/components/icons";

const extOf = (name) => (name.includes(".") ? name.split(".").pop().toUpperCase() : "");
const COLS = "grid-cols-[minmax(0,1fr)_140px] lg:grid-cols-[minmax(0,1fr)_70px_90px_160px]";

const Passages = ({ doc }) => {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setRows(null);
    setFailed(false);
    api
      .getPassages(doc.id)
      .then((r) => live && setRows(r))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [doc.id]);

  if (failed) return <p className="text-sm text-muted-foreground">Could not load passages.</p>;
  if (!rows) return <p className="text-sm text-muted-foreground">Loading passages…</p>;
  if (!rows.length) return <p className="text-sm text-muted-foreground">No passages were indexed for this file.</p>;
  return (
    <>
      {rows.map((p, i) => (
        <div key={p.id} className="paper-inset px-4 py-3.5 rounded-xl text-sm leading-[1.55] text-soft">
          <span className="font-mono text-xs text-muted-foreground">Passage {i + 1}</span>
          <br />
          <span className="whitespace-pre-wrap break-words">
            {p.text.length > 320 ? `${p.text.slice(0, 320)}…` : p.text}
          </span>
        </div>
      ))}
      {doc.chunk_count > rows.length && (
        <p className="text-xs text-muted-foreground font-mono">Showing first {rows.length} of {doc.chunk_count}</p>
      )}
    </>
  );
};

export const KnowledgeBase = ({ documents, refresh }) => {
  const [dragging, setDragging] = useState(false);
  const [pending, setPending] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback(
    async (files) => {
      const list = Array.from(files);
      if (!list.length) return;
      const items = list.map((file, i) => ({ key: `${Date.now()}_${i}`, file }));
      setPending((p) => [...p, ...items.map(({ key, file }) => ({ key, name: file.name, progress: 0, error: null }))]);
      const patch = (key, next) => setPending((p) => p.map((x) => (x.key === key ? { ...x, ...next } : x)));
      for (const { key, file } of items) {
        try {
          await api.uploadDocument(file, (progress) => patch(key, { progress }));
          toast.success(`Indexed “${file.name}”`);
          setPending((p) => p.filter((x) => x.key !== key));
          refresh();
        } catch (e) {
          patch(key, { error: e?.response?.data?.detail || "Could not read file" });
        }
      }
    },
    [refresh]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const totalChunks = documents.reduce((s, d) => s + d.chunk_count, 0);
  const selected = documents.find((d) => d.id === selectedId) || documents[0] || null;

  const removeDoc = async (doc) => {
    await api.deleteDocument(doc.id);
    toast.success(`Removed “${doc.name}”`);
    refresh();
  };

  return (
    <section className="flex flex-col gap-5 lg:gap-7 fade-up" data-testid="knowledge-base-section">
      <div className="flex justify-between items-end gap-4">
        <div className="flex flex-col gap-1.5 min-w-0">
          <h1 className="font-heading text-[40px] lg:text-5xl leading-none tracking-[-0.02em]">Sources</h1>
          <p className="text-[15px] lg:text-base text-soft">
            {documents.length} document{documents.length === 1 ? "" : "s"} · {totalChunks} passages indexed. The
            assistant answers only from these.
          </p>
        </div>
        <div className="flex gap-3 shrink-0">
          {documents.length > 0 &&
            (confirmClear ? (
              <div className="flex items-center gap-2 text-sm">
                <button
                  data-testid="clear-kb-confirm-button"
                  onClick={async () => {
                    await api.clearDocuments();
                    toast.success("Knowledge base cleared");
                    setConfirmClear(false);
                    refresh();
                  }}
                  className="h-[46px] px-4 rounded-xl border border-destructive text-destructive font-medium"
                >
                  Yes, clear
                </button>
                <button onClick={() => setConfirmClear(false)} className="h-[46px] px-3 text-soft underline">
                  Cancel
                </button>
              </div>
            ) : (
              <button
                data-testid="clear-kb-button"
                onClick={() => setConfirmClear(true)}
                className="h-[46px] px-5 rounded-xl border border-destructive text-destructive text-[15px] font-medium"
              >
                Clear all
              </button>
            ))}
          <button
            data-testid="add-sources-button"
            onClick={() => inputRef.current?.click()}
            className="paper-dark hidden sm:flex h-[46px] px-[22px] items-center gap-2.5 rounded-xl text-primary-foreground text-[15px] font-medium"
          >
            <PlusIcon size={16} /> Add sources
          </button>
        </div>
      </div>

      {/* Dropzone */}
      <div
        data-testid="markdown-drag-drop-zone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`min-h-[132px] shrink-0 rounded-2xl border-[1.5px] border-dashed border-[#8E8878] flex items-center justify-center gap-5 px-4 cursor-pointer transition-colors ${
          dragging ? "bg-accent/30" : "bg-cream"
        }`}
      >
        <input
          ref={inputRef}
          data-testid="markdown-file-input"
          type="file"
          accept=".md,.markdown,.txt,.pdf,.docx,.csv"
          multiple
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <div className="w-12 h-12 shrink-0 rounded-full bg-accent border border-foreground flex items-center justify-center">
          <UploadIcon size={22} />
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-lg font-semibold">
            Drop files here or <span data-testid="upload-markdown-button">browse</span>
          </div>
          <div className="font-mono text-[13px] text-muted-foreground">.md · .txt · .pdf · .docx · .csv</div>
        </div>
      </div>

      <div className="grid gap-5 lg:gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
        {/* Table */}
        <div data-testid="document-list" className="paper rounded-2xl border border-border overflow-hidden self-start w-full">
          <div
            className={`h-12 px-4 lg:px-6 grid ${COLS} gap-4 items-center border-b border-border font-mono text-xs tracking-[0.05em] uppercase text-muted-foreground`}
          >
            <span>Name</span>
            <span className="hidden lg:block">Type</span>
            <span className="hidden lg:block">Passages</span>
            <span>Status</span>
          </div>

          {pending.map((p) => (
            <div key={p.key} className={`min-h-[72px] px-4 lg:px-6 grid ${COLS} gap-4 items-center border-b border-border last:border-b-0`}>
              <span className="text-base font-semibold truncate">{p.name}</span>
              <span className="hidden lg:block font-mono text-[13px]">{extOf(p.name)}</span>
              <span className="hidden lg:block text-[15px] text-muted-foreground">{p.error ? "n/a" : "…"}</span>
              {p.error ? (
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-destructive" title={p.error}>Could not read file</span>
                  <button
                    onClick={() => setPending((x) => x.filter((i) => i.key !== p.key))}
                    className="self-start text-xs text-muted-foreground underline"
                  >
                    Dismiss
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium">Indexing {p.progress}%</span>
                  <div className="h-1.5 rounded-sm bg-track overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${p.progress}%` }} />
                  </div>
                </div>
              )}
            </div>
          ))}

          {documents.map((doc) => {
            const active = selected?.id === doc.id;
            return (
              <button
                key={doc.id}
                type="button"
                data-testid={`document-item-${doc.id}`}
                onClick={() => setSelectedId(doc.id)}
                aria-pressed={active}
                className={`w-full text-left min-h-[72px] px-4 lg:px-6 grid ${COLS} gap-4 items-center border-b border-border last:border-b-0 transition-colors ${
                  active ? "bg-white/50" : "hover:bg-white/30"
                }`}
              >
                <span className="text-base font-semibold truncate">{doc.name}</span>
                <span className="hidden lg:block font-mono text-[13px]">{extOf(doc.name)}</span>
                <span className="hidden lg:block text-[15px]">{doc.chunk_count}</span>
                <span className="flex items-center gap-2 text-sm font-medium text-success">
                  <CheckIcon size={16} /> Indexed
                </span>
              </button>
            );
          })}

          {documents.length === 0 && pending.length === 0 && (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No documents yet. Upload one to get started.
            </div>
          )}
        </div>

        {/* Detail */}
        {selected && (
          <div className="paper rounded-2xl border border-border p-6 flex flex-col gap-4 self-start">
            <div className="flex justify-between items-center gap-3">
              <span className="text-lg font-semibold truncate">{selected.name}</span>
              <button
                data-testid={`delete-document-button-${selected.id}`}
                aria-label={`Delete ${selected.name}`}
                onClick={() => removeDoc(selected)}
                className="w-10 h-10 shrink-0 rounded-[10px] border border-border text-destructive flex items-center justify-center hover:bg-destructive/5"
              >
                <TrashIcon />
              </button>
            </div>
            <div className="font-mono text-xs tracking-[0.05em] uppercase text-muted-foreground">
              Passages the assistant can cite
            </div>
            <Passages doc={selected} />
          </div>
        )}
      </div>
    </section>
  );
};
